import { IconButton, ViewPlaceholder } from '@vrplatform/components';
import { useApiQuery, useQuery } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { Drawer } from '@vrplatform/components-legacy';
import { type Maybe, formatCurrency, sum, utc } from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  LoadingOverlay,
  Skeleton,
  Stack,
  Title,
  rem,
} from '@mantine/core';
import { Text } from '@mantine/core';
import { getListingName } from '@vrplatform/ui-common';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  getDepositManualLinesCentTotal,
  getDepositReservationLinesCentTotal,
} from '../../views/deposits/edit/_utils';
import { DepositEllipsisMenuItems } from '../../views/deposits/list/DepositEllipsisMenuItems';
import { DepositEllipsisMenuModals } from '../../views/deposits/list/DepositEllipsisMenuModals';
import { useListingDetailDrawer } from '../../views/listings/drawer';
import { DepositLineAmountHoverCard } from '../../views/reconciliation/_components';
import { DrawerCollapsableTable, DrawerHeader } from '../_components';
import { useReservationDetailDrawer } from '../reservation-drawers';
import { DepositInfoCard } from './DepositInfoCard';
import { useDepositDetailDrawer } from './useDepositDetailDrawer';
import { isOpeningBalanceTransaction } from '@vrplatform/embed';
import { DepositExpenseTransferIssues } from '../expense-drawer/DepositExpenseTransferIssues';
import { useDepositQuery } from '../../views/deposits/detail/useDepositQuery';
import { AppIconById, TagParty } from '../../components';
import type { DepositRow } from '../../views/deposits/list/useDepositsTableQuery';

export const DepositDetailDrawer = () => {
  const goto = useNavigate();

  const { opened, close: closeDrawer, depositId } = useDepositDetailDrawer();
  const { isLoading, data } = useDepositQuery(depositId, 'drawer');
  const [openedModal, setOpenedModal] = useState<
    'delete' | 'edit' | 'unreconcile' | 'archive' | null
  >(null);

  const deposit = data?.deposit;

  const closeModal = useCallback(() => setOpenedModal(null), []);

  const handlers = useMemo(
    () => ({
      openDeleteModal: () => setOpenedModal('delete'),
      openUnreconcileModal: () => setOpenedModal('unreconcile'),
      openArchiveModal: () => setOpenedModal('archive'),
    }),
    []
  );
  const openingBalance = isOpeningBalanceTransaction(deposit?.uniqueRef);

  return (
    <>
      {depositId && (
        <DepositEllipsisMenuModals
          deposit={deposit}
          deleteModal={{
            closeModal,
            opened: openedModal === 'delete',
            onSuccess: () => {
              if (window.location.pathname.startsWith('/deposit/'))
                goto('/deposits');
              else closeDrawer();
            },
          }}
          unreconcileModal={{
            closeModal,
            opened: openedModal === 'unreconcile',
          }}
          archiveModal={{
            closeModal,
            opened: openedModal === 'archive',
          }}
        />
      )}

      <Drawer opened={opened} onClose={closeDrawer} size={550}>
        <DrawerHeader
          closeDrawer={closeDrawer}
          loading={isLoading}
          type="Deposit"
          title={
            deposit && (
              <Group wrap="nowrap" gap="xs" mt={rem(5)}>
                <Center w={20}>
                  {deposit.connection?.appId ? (
                    <AppIconById appId={deposit.connection.appId} />
                  ) : (
                    <Icon icon="UserIcon" size={20} />
                  )}
                </Center>
                <Box>
                  <Title order={4} fw={500} component="p" m={0}>
                    {deposit.connection?.name || 'Manual deposit'}
                  </Title>
                </Box>
              </Group>
            )
          }
          menuItems={
            deposit && (
              <DepositEllipsisMenuItems
                deposit={deposit}
                openDeleteModal={handlers.openDeleteModal}
                openUnreconcileModal={handlers.openUnreconcileModal}
                openArchiveModal={handlers.openArchiveModal}
                isDrawer
              />
            )
          }
        >
          {deposit && (
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                if (openingBalance) {
                  if (openingBalance.type !== 'listing') {
                    goto(`/settings/accounting/opening-balances`);
                  }
                  return;
                }

                goto(`/deposit/${deposit.id}`);
              }}
              icon="ArrowRightCircleIcon"
              variant="outline"
              tooltip="Go to detail"
            />
          )}
        </DrawerHeader>
        {!deposit && !isLoading ? (
          <ViewPlaceholder
            title="No deposit found"
            description="Has the deposit been deleted?"
            icon="CommentQuestionIcon"
            backgroundColor="transparent"
          />
        ) : (
          <Content deposit={deposit} isLoading={isLoading} />
        )}
      </Drawer>
    </>
  );
};

const Content = ({
  deposit,
  isLoading,
}: {
  deposit: Maybe<DepositRow>;
  isLoading: boolean;
}) => {
  if (!deposit) return null;

  return (
    <Stack
      gap={'md'}
      mb="md"
      sx={{
        position: 'relative',
      }}
    >
      <DepositExpenseTransferIssues issues={deposit.issues} type="deposit" />

      <DepositInfoCard deposit={deposit} type="drawer" />

      {!!deposit.lines.unassignedLines.length && (
        <UnassignedLines
          lines={deposit.lines.unassignedLines}
          currency={deposit.currency}
          title="Unassigned items"
        />
      )}

      <Reservations
        rowData={deposit.lines.reservations}
        currency={deposit.currency}
        title="Reservations"
      />

      <Manuals
        rowData={deposit.lines.manuals}
        currency={deposit.currency}
        title="Non-Reservation"
      />

      <LoadingOverlay
        visible={isLoading}
        loaderProps={{
          size: 'sm',
        }}
      />
    </Stack>
  );
};

const Reservations = ({
  rowData,
  currency,
  title,
}: {
  rowData: DepositRow['lines']['reservations'];
  title: string;
  currency: string;
}) => {
  const { open: openReservation } = useReservationDetailDrawer();

  const total =
    getDepositReservationLinesCentTotal(rowData.map((x) => x)) / 100;

  const columns = useMemo<
    MRT_ColumnDef<DepositRow['lines']['reservations'][0]>[]
  >(
    () => [
      {
        header: 'Description',
        accessorKey: 'description',
        Cell: ({ row }) => {
          const queryData = useApiQuery(
            async (api, signal, args) => {
              return await api
                .GET('/reservations/{id}', {
                  params: {
                    path: {
                      id: args.id,
                    },
                  },
                  signal,
                })
                .then((result) => {
                  if (result.error) throw result.error;
                  return {
                    reservation: result.data,
                  };
                });
            },
            {
              variables: {
                id: row.original.reservationId,
              },
              queryGroup: 'deposits',
              uniqueQueryKey: `reservation-row-detail-${row.original.reservationId}`,
              queryKeys: ['reservations'],
              keepPreviousData: true,
            }
          );

          const reservation = queryData.data?.reservation;

          if (queryData.isLoading)
            return <Skeleton height={24} width={'100%'} />;

          if (!reservation) return '-';

          return (
            <Group wrap="nowrap" w="100%">
              {reservation.connection?.id && (
                <AppIconById appId={reservation.connection.appId} />
              )}
              <Box flex={1} maw="calc(100% - 40px)">
                <Text component="p">{reservation.guestName}</Text>
                <Text component="p" c="gray" size="xs" truncate>
                  {`${utc(reservation.checkIn).format('ll')} - ${utc(
                    reservation.checkOut
                  ).format(
                    'll'
                  )}${reservation.guests ? ` - ${reservation.guests} guests` : ''}`}
                </Text>
                {reservation.listing?.id && (
                  <Text
                    component="p"
                    c="gray"
                    size="xs"
                    truncate
                    display="block"
                    maw="100%"
                  >
                    {reservation.listing.name}
                  </Text>
                )}
              </Box>
            </Group>
          );
        },
      },
      {
        header: 'amount',
        accessorKey: 'centTotal',
        maxSize: 0,
        minSize: 90,
        size: 90,
        mantineTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => {
          return (
            <DepositLineAmountHoverCard {...row.original} currency={currency} />
          );
        },
      },
    ],
    [currency]
  );

  return (
    <DrawerCollapsableTable
      title={title}
      rightSection={
        <Text size="sm" fw={500}>
          {formatCurrency(total, currency)}
        </Text>
      }
      rowData={rowData}
      columns={columns}
      onRowClick={{
        handler: (row) => {
          if (row.original.reservationId)
            openReservation(row.original.reservationId, 'overview', 'push');
        },
        disabled: (row) => !row.original.reservationId,
      }}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" c="gray">
            No reservations found
          </Text>
        </Center>
      )}
    />
  );
};

type DepositManualLine = DepositRow['lines']['manuals'][number];

const getDepositManualLineLookupQueryKey = (line: DepositManualLine) =>
  `deposit-manual-line-${line.transactionLineId ?? line.paymentLineId ?? line.merchantFeeLineId ?? 'unknown'}`;

const useDepositManualLineLookupQuery = (line: DepositManualLine) => {
  return useApiQuery(
    async (api, signal, args) => {
      const [contact, listing, account] = await Promise.all([
        !args.contactId
          ? null
          : api
              .GET('/contacts/{id}', {
                params: {
                  path: {
                    id: args.contactId,
                  },
                },
                signal,
              })
              .then((result) => {
                if (result.error) return null;
                return result.data;
              }),
        !args.listingId
          ? null
          : api
              .GET('/listings/{id}', {
                params: {
                  path: {
                    id: args.listingId,
                  },
                },
                signal,
              })
              .then((result) => {
                if (result.error) return null;
                return result.data;
              }),
        !args.accountId
          ? null
          : api
              .GET('/accounts/{id}', {
                params: {
                  path: {
                    id: args.accountId,
                  },
                },
                signal,
              })
              .then((result) => {
                if (result.error) return null;
                return result.data;
              }),
      ]);

      return {
        contact,
        listing,
        account,
      };
    },
    {
      variables: {
        contactId: line.contactId,
        listingId: line.listingId,
        accountId: line.accountId,
      },
      uniqueQueryKey: getDepositManualLineLookupQueryKey(line),
      queryGroup: 'deposits',
      queryKeys: ['deposits'],
      keepPreviousData: true,
    }
  );
};

const ManualLineDescriptionCell = ({ line }: { line: DepositManualLine }) => {
  const queryData = useDepositManualLineLookupQuery(line);

  if (queryData.isLoading) return <Skeleton height={24} width={'100%'} />;

  const contact = queryData.data?.contact;
  const listing = queryData.data?.listing;
  const description = line.description;

  if (!contact && !listing) return description;

  return (
    <Box>
      {contact && (
        <Group wrap="nowrap" w="100%">
          <Icon icon="UserIcon" size={16} />
          <Text lineClamp={2}>{contact.name}</Text>
        </Group>
      )}
      {listing && (
        <Group wrap="nowrap" w="100%">
          <Icon icon="HomeIcon" size={16} />
          <Text lineClamp={2}>{listing.name}</Text>
        </Group>
      )}
      {description && (
        <Text size="xs" c="gray" lineClamp={3}>
          {description}
        </Text>
      )}
    </Box>
  );
};

const ManualLineAccountPartyCell = ({ line }: { line: DepositManualLine }) => {
  const queryData = useDepositManualLineLookupQuery(line);

  if (queryData.isLoading) return <Skeleton height={24} width={'100%'} />;

  return (
    <Stack>
      <Text>{queryData.data?.account?.name ?? '-'}</Text>
      {line.party ? (
        <TagParty party={line.party} />
      ) : (
        <Text size="xs" c="gray">
          -
        </Text>
      )}
    </Stack>
  );
};

const Manuals = ({
  rowData,
  currency,
  title,
}: {
  rowData: DepositRow['lines']['manuals'];
  title: string;
  currency: string;
}) => {
  const total = getDepositManualLinesCentTotal(rowData) / 100;

  const columns = useMemo<MRT_ColumnDef<DepositRow['lines']['manuals'][0]>[]>(
    () => [
      {
        header: 'Description',
        accessorKey: 'description',
        Cell: ({ row }) => {
          return <ManualLineDescriptionCell line={row.original} />;
        },
      },
      {
        header: 'Account / Party',
        accessorKey: 'accountId',
        Cell: ({ row }) => {
          return <ManualLineAccountPartyCell line={row.original} />;
        },
      },
      {
        header: 'amount',
        accessorKey: 'centTotal',
        maxSize: 0,
        minSize: 90,
        size: 90,
        mantineTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => {
          return (
            <DepositLineAmountHoverCard {...row.original} currency={currency} />
          );
        },
      },
    ],
    [currency]
  );

  return (
    <DrawerCollapsableTable
      title={title}
      rightSection={
        <Text size="sm" fw={500}>
          {formatCurrency(total, currency)}
        </Text>
      }
      rowData={rowData}
      columns={columns}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" c="gray">
            No lines found
          </Text>
        </Center>
      )}
    />
  );
};

const UnassignedLines = ({
  lines,
  currency,
  title,
}: {
  lines: DepositRow['lines']['unassignedLines'];
  currency: string;
  title: string;
}) => {
  const { open: openReservation } = useReservationDetailDrawer();
  const { open: openListing } = useListingDetailDrawer();

  const total = useMemo(
    () => formatCurrency(sum(lines, (x) => x.centTotal || 0) / 100, currency),
    [currency, lines]
  );

  const { data: rowData = [], isLoading: loading } = useQuery(
    (q, args) => {
      return q
        .transactionLines({
          where: {
            id: { _in: args.ids },
          },
          order_by: [{ createdAt: 'desc' }],
        })
        .map((line) => ({
          id: line.id,
          description: line.description,
          listing: {
            id: line.listingId,
            name: getListingName(line.listing),
          },
          reservation: {
            id: line.reservationId,
            guestName: line.reservation?.guestName,
            confirmationCode: line.reservation?.confirmationCode,
          },
          assignment: line.accountAssignmentType,
        }));
    },
    {
      variables: {
        ids: lines.map((x) => x.transactionLineId),
      },
      queryKey: ['deposits'],
    }
  );

  type Row = (typeof rowData)[number];

  const columns = useMemo<MRT_ColumnDef<Row>[]>(
    () => [
      {
        header: 'Description',
        accessorKey: 'description',
        Cell: ({ row }) => {
          if (!row.original.reservation.id) {
            return row.original.description;
          }

          return (
            <Box>
              <Text component="span" display="block" size="sm">
                {row.original.reservation.guestName}
              </Text>
              <Text component="span" display="block" size="xs" c="gray">
                {row.original.reservation.confirmationCode}
              </Text>
            </Box>
          );
        },
      },
      {
        header: 'Type',
        accessorKey: 'type',
        mantineTableBodyCellProps: {
          align: 'right',
        },
        size: 100,
        Cell: ({ row }) => {
          const line = lines.find(
            (x) => x.transactionLineId === row.original.id
          );
          if (!line) return null;

          return line.type;
        },
      },
      {
        header: 'CentTotal',
        accessorKey: 'centTotal',
        mantineTableBodyCellProps: {
          align: 'right',
        },
        size: 100,
        Cell: ({ row }) => {
          const line = lines.find(
            (x) => x.transactionLineId === row.original.id
          );
          if (!line) return null;

          return (
            <DepositLineAmountHoverCard
              {...line}
              paymentCentTotal={line.centTotal}
              currency={currency}
            />
          );
        },
      },
    ],
    [currency, lines]
  );

  if (!lines.length) return null;

  return (
    <DrawerCollapsableTable
      title={title}
      onRowClick={{
        handler: (row) => {
          if (row.original.reservation.id)
            return openReservation(
              row.original.reservation.id,
              'overview',
              'push'
            );

          if (row.original.listing.id)
            return openListing(row.original.listing.id, 'overview', 'push');
        },
        disabled: (row) =>
          !row.original.reservation?.id && !row.original.listing?.id,
      }}
      rightSection={
        <Text fw={500} size="sm">
          {total}
        </Text>
      }
      rowData={rowData}
      columns={columns}
      loading={loading}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" c="gray">
            No lines available
          </Text>
        </Center>
      )}
    />
  );
};
